import styled from 'styled-components';

export const PageWrapper = styled.div`
  padding: 2rem;
  max-width: 1280px;
  margin: 0 auto;
`;

export const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.25rem;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

export const Subtitle = styled.p`
  margin: 0.25rem 0 0;
  color: var(--text-secondary, var(--text-muted, #64748b));
  font-size: 0.9375rem;
`;

export const InsightHero = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  margin-bottom: 1.25rem;
  background: linear-gradient(
    135deg,
    var(--accent-subtle, rgba(15, 118, 110, 0.12)),
    var(--bg-surface, var(--bg-elevated, transparent))
  );
  border: 1px solid var(--border);
  border-radius: 12px;
`;

export const InsightLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--accent);
`;

export const InsightText = styled.p`
  margin: 0;
  font-size: 1.0625rem;
  line-height: 1.5;
  color: var(--text-primary);
  max-width: 52rem;

  strong {
    color: var(--accent);
    font-weight: 600;
  }
`;

export const InsightActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const AskSection = styled.section`
  margin-bottom: 1.5rem;
`;

export const AskBarWrap = styled.div`
  position: relative;
  width: 100%;
`;

export const AskBarIcon = styled.span`
  position: absolute;
  left: 1.25rem;
  top: 50%;
  transform: translateY(-50%);
  color: var(--accent);
  font-size: 1.25rem;
  pointer-events: none;
  line-height: 1;
`;

export const AskBarInput = styled.input`
  width: 100%;
  padding: 1.125rem 1.25rem 1.125rem 3.25rem;
  background: var(--cmd-bg, var(--bg-surface));
  border: 2px solid var(--border);
  border-radius: 16px;
  font-family: inherit;
  font-size: 1.0625rem;
  color: var(--text-primary);
  transition: border-color 0.2s, box-shadow 0.2s;
  outline: none;
  box-shadow: var(--shadow-md);

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
    box-shadow: 0 0 0 4px var(--ring), var(--shadow-lg);
  }
`;

export const AssistantPanel = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 420px;
`;

export const AssistantWelcome = styled.div`
  margin-bottom: 1rem;
`;

export const AssistantWelcomeIcon = styled.div`
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  color: var(--accent);
`;

export const AssistantMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 0 0 1rem;
  min-height: 120px;
`;

export const AssistantMessage = styled.div`
  display: flex;
  gap: 0.75rem;
  max-width: 100%;
`;

export const AssistantAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 600;
  flex-shrink: 0;
  background: var(--accent-subtle);
  color: var(--accent);
`;

export const AssistantBubble = styled.div`
  padding: 0.875rem 1.125rem;
  border-radius: 16px;
  border-top-left-radius: 4px;
  font-size: 0.9375rem;
  line-height: 1.6;
  background: var(--bg-elevated, var(--bg-inset));
  border: 1px solid var(--border);
  color: var(--text-primary);

  strong {
    color: var(--accent);
    font-weight: 600;
  }
`;

export const ChatInputArea = styled.div`
  padding-top: 1rem;
  border-top: 1px solid var(--border);
  margin-top: auto;
`;

export const ChatInputWrap = styled.form`
  display: flex;
  align-items: flex-end;
  gap: 0.75rem;
`;

export const ChatTextarea = styled.textarea`
  flex: 1;
  padding: 0.875rem 1rem;
  background: var(--input-bg, var(--bg-elevated));
  border: 1px solid var(--border);
  border-radius: 12px;
  font-family: inherit;
  font-size: 0.9375rem;
  color: var(--text-primary);
  outline: none;
  resize: none;
  max-height: 160px;
  line-height: 1.5;
  transition: border-color 0.2s;

  &::placeholder {
    color: var(--text-muted);
  }

  &:focus {
    border-color: var(--accent);
  }
`;

export const ChatSendButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: var(--accent);
  color: var(--text-inverse, #fff);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.125rem;
  flex-shrink: 0;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: var(--accent-hover);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const TokenMixBar = styled.div`
  display: flex;
  height: 12px;
  border-radius: 6px;
  overflow: hidden;
  background: var(--bg-muted);
  margin: 0.75rem 0 0.5rem;
`;

export const TokenMixSegment = styled.div<{ $pct: number; $tone: 'in' | 'out' }>`
  width: ${({ $pct }) => Math.max(0, Math.min(100, $pct))}%;
  background: ${({ $tone }) =>
    $tone === 'in' ? 'var(--accent)' : 'var(--warning)'};
`;

export const TokenMixLegend = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

export const ControlsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-bottom: 1.25rem;
`;

export const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  flex-wrap: wrap;
`;

export const ControlLabel = styled.span`
  font-size: 0.75rem;
  color: var(--text-secondary, #64748b);
  margin-right: 0.25rem;
`;

export const RangeText = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.75rem;
  background: var(--bg-surface, var(--bg-elevated, transparent));
  border: 1px solid var(--border);
  border-radius: 8px;
  font-size: 0.8125rem;
  color: var(--text-secondary, #64748b);
`;

export const KpiStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 0.75rem;

  @media (max-width: 900px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

export const KpiCard = styled.div`
  background: var(--bg-surface, var(--bg-elevated, transparent));
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
`;

export const KpiLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--text-secondary, #64748b);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

export const KpiValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: var(--text-primary);
`;

export const KpiChange = styled.div<{ $tone?: 'up' | 'down' | 'neutral' }>`
  font-size: 0.75rem;
  font-weight: 500;
  margin-top: 0.25rem;
  color: ${({ $tone }) =>
    $tone === 'up'
      ? 'var(--success)'
      : $tone === 'down'
        ? 'var(--error)'
        : 'var(--text-secondary)'};
`;

export const SecondaryDisclosure = styled.details`
  margin-bottom: 1.25rem;
  font-size: 0.875rem;
  color: var(--text-secondary);

  summary {
    cursor: pointer;
    color: var(--text-primary);
  }

  p {
    margin: 0.5rem 0 0;
  }

  a {
    color: var(--accent);
  }
`;

export const DimensionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.375rem;
  margin-bottom: 1.25rem;
  flex-wrap: wrap;
`;

export const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 1.25rem;
  margin-bottom: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const PanelCard = styled.div`
  background: var(--bg-surface, var(--bg-elevated, transparent));
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
`;

export const SectionTitle = styled.h3`
  margin: 0 0 0.25rem;
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
`;

export const SectionSub = styled.p`
  margin: 0 0 1rem;
  font-size: 0.8125rem;
  color: var(--text-secondary, #64748b);
`;

export const PanelEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 180px;
  border: 1px dashed var(--border);
  border-radius: 8px;
  color: var(--text-secondary, #64748b);
  font-size: 0.875rem;
  text-align: center;
  padding: 1rem;
`;

export const ChartLegend = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid var(--border);
  font-size: 0.8125rem;
  color: var(--text-secondary, #64748b);
`;

export const BreakdownTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;

  th {
    text-align: left;
    font-weight: 500;
    color: var(--text-secondary, #64748b);
    padding: 0.5rem 0;
    border-bottom: 1px solid var(--border);
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  td {
    padding: 0.75rem 0;
    border-bottom: 1px solid var(--border);
    color: var(--text-primary);
  }

  tr:last-child td {
    border-bottom: none;
  }

  .mono {
    font-variant-numeric: tabular-nums;
  }

  a {
    color: var(--accent);
    text-decoration: none;
  }

  a:hover {
    text-decoration: underline;
  }
`;

export const BudgetRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.875rem 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

export const BudgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.875rem;
`;

export const Progress = styled.div<{ $pct: number }>`
  height: 8px;
  border-radius: 4px;
  background: var(--bg-muted);
  overflow: hidden;

  &::after {
    content: '';
    display: block;
    height: 100%;
    width: ${({ $pct }) => Math.min(100, Math.max(0, $pct))}%;
    border-radius: 4px;
    background: ${({ $pct }) =>
      $pct >= 90
        ? 'var(--error)'
        : $pct >= 70
          ? 'var(--warning)'
          : 'var(--accent)'};
  }
`;

export const BudgetActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: var(--bg-overlay);
  z-index: 39;
`;

export const SpotSlideOver = styled.aside`
  position: fixed;
  top: 0;
  right: 0;
  width: min(420px, 100vw);
  height: 100vh;
  background: var(--bg-elevated, var(--bg-surface));
  border-left: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
  z-index: 40;
  display: flex;
  flex-direction: column;
`;

export const SpotHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

export const SpotBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
`;

export const SpotRow = styled.div`
  padding: 0.875rem 0;
  border-bottom: 1px solid var(--border);

  &:last-of-type {
    border-bottom: none;
  }
`;

export const SpotTop = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.75rem;
  font-weight: 500;
  font-size: 0.875rem;
`;

export const SpotMeta = styled.div`
  font-size: 0.75rem;
  color: var(--text-secondary, #64748b);
  margin-top: 0.25rem;
`;

export const SpotTokens = styled.div`
  font-size: 0.8125rem;
  margin-top: 0.35rem;
  color: var(--text-primary);
`;

export const EmptyShell = styled.div`
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--bg-surface, var(--bg-elevated, transparent));
  margin-bottom: 1.25rem;
`;

export const TwoPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 1.25rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const PortletGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
  }
`;

export const PortletFull = styled.div`
  grid-column: 1 / -1;
`;
